import { create } from "@/lib/create-safe-store";
import { getStorage } from "@/lib/storage";
import type { Account } from "./types";
import type { PendingInvitation } from "./invitations-api";
import { writeActiveAccountId } from "./persistence";

interface AccountState {
  accounts: Account[];
  pendingInvitations: PendingInvitation[];
  activeAccountId: string | null;
  setAccounts: (accounts: Account[]) => void;
  setPendingInvitations: (invitations: PendingInvitation[]) => void;
  setActiveAccountId: (id: string | null) => void;
  activeAccount: () => Account | undefined;
}

export const useAccountStore = create<AccountState>((set, get) => ({
  accounts: [],
  pendingInvitations: [],
  activeAccountId: null,
  setAccounts: (accounts) => {
    set({ accounts });
    getStorage().saveAccounts(accounts).catch(() => {});
  },
  setPendingInvitations: (pendingInvitations) => set({ pendingInvitations }),
  setActiveAccountId: (id) => {
    set({ activeAccountId: id });
    writeActiveAccountId(id);
  },
  activeAccount: () => {
    const { accounts, activeAccountId } = get();
    return accounts.find((a) => a.id === activeAccountId);
  },
}));

/**
 * Switch to a different account: reset all per-account state so the UI
 * doesn't flash stale data from the previous account.
 */
export async function switchAccount(accountId: string) {
  const { unsubscribeConversation } = await import("@/modules/realtime/handlers");

  // Unsubscribe from the currently-open conversation channel
  unsubscribeConversation();

  // Set the new active account (triggers conversation list reload via useEffect)
  // Stores keep their data — conversations loaded from cache, then synced from server
  useAccountStore.getState().setActiveAccountId(accountId);
}

if (typeof window !== "undefined") {
  (window as any).__accountStore = useAccountStore;
}
